import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { clientsAPI, entitiesAPI, exportAPI } from '../../services/api';
import { COUNTRY_NAMES } from '../../constants/compliance';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Building2, Plus, Search, Edit, Trash2, MoreHorizontal, Eye, 
  Users, ChevronRight, Globe, Mail, Phone, Building, Layers, Download
} from 'lucide-react';
import { toast } from 'sonner';

const CLIENT_TYPES = [
  { value: 'group', label: 'Group of Companies', description: 'Holding company with subsidiaries' },
  { value: 'standalone', label: 'Standalone Company', description: 'Independent organization' },
  { value: 'ngo', label: 'NGO', description: 'Non-governmental organization' },
  { value: 'government', label: 'Government', description: 'Government entity or agency' },
];

const INDUSTRIES = [
  'Financial Services', 'Healthcare', 'Technology', 'Retail', 'Manufacturing',
  'Education', 'Hospitality', 'Real Estate', 'Legal Services', 'Telecommunications',
  'Energy', 'Transportation', 'Media & Entertainment', 'Agriculture', 'Other'
];

const COUNTRIES = COUNTRY_NAMES;

const STATUS_COLORS = {
  active: 'bg-green-500/10 text-green-600 border-green-500/20',
  inactive: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
  onboarding: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
};

const MATURITY_TIERS = [
  { value: 'T1', label: 'T1 - Foundational', description: '34 controls for small organizations', controls: 34 },
  { value: 'T2', label: 'T2 - Standard', description: '71 controls for mid-sized organizations', controls: 71 },
  { value: 'T3', label: 'T3 - Advanced', description: '84 controls for large enterprises', controls: 84 },
];

export default function ClientManagementPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [formData, setFormData] = useState({
    client_name: '',
    client_type: 'standalone',
    industry: '',
    country: '',
    address: '',
    contact_email: '',
    contact_phone: '',
    website: '',
    notes: '',
    status: 'active',
    maturity_tier: 'T1',
    // Subscription tier — controls which modules the client gets at creation
    // (seeded from the matching template per "Fluid Module Assignment" spec).
    // Just a marketing label after creation — modules are independent of tier.
    subscription_tier: 'starter',
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await clientsAPI.getAll();
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to fetch clients');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Convert empty strings to null for optional fields to avoid Pydantic validation errors
      const cleanedData = { ...formData };
      ['contact_email', 'contact_phone', 'website', 'address', 'notes', 'industry', 'country'].forEach(field => {
        if (cleanedData[field] === '') {
          cleanedData[field] = null;
        }
      });
      
      if (editingClient) {
        await clientsAPI.update(editingClient.id, cleanedData);
        toast.success('Client updated successfully');
      } else {
        await clientsAPI.create(cleanedData);
        toast.success('Client created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchClients();
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Handle Pydantic validation errors (array of objects) vs simple string errors
      const errorMessage = Array.isArray(detail) 
        ? detail.map(e => e.msg || e.message).join(', ')
        : (typeof detail === 'string' ? detail : 'Failed to save client');
      toast.error(errorMessage);
    }
  };

  const handleDelete = async () => {
    if (!clientToDelete) return;
    try {
      await clientsAPI.delete(clientToDelete.id);
      toast.success('Client deleted successfully');
      setDeleteDialogOpen(false);
      setClientToDelete(null);
      fetchClients();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMessage = Array.isArray(detail) 
        ? detail.map(e => e.msg || e.message).join(', ')
        : (typeof detail === 'string' ? detail : 'Failed to delete client');
      toast.error(errorMessage);
    }
  };

  const resetForm = () => {
    setFormData({
      client_name: '',
      client_type: 'standalone',
      industry: '',
      country: '',
      address: '',
      contact_email: '',
      contact_phone: '',
      website: '',
      notes: '',
      status: 'active',
      maturity_tier: 'T1',
      subscription_tier: 'starter',
    });
    setEditingClient(null);
  };

  const openEditDialog = (client) => {
    setEditingClient(client);
    setFormData({
      client_name: client.client_name || '',
      client_type: client.client_type || 'standalone',
      industry: client.industry || '',
      country: client.country || '',
      address: client.address || '',
      contact_email: client.contact_email || '',
      contact_phone: client.contact_phone || '',
      website: client.website || '',
      notes: client.notes || '',
      status: client.status || 'active',
      maturity_tier: client.maturity_tier || 'T1',
      subscription_tier: client.subscription_tier || 'starter',
    });
    setDialogOpen(true);
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = client.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.client_code?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || client.client_type === filterType;
    return matchesSearch && matchesType;
  });

  const stats = {
    total: clients.length,
    groups: clients.filter(c => c.client_type === 'group').length,
    standalone: clients.filter(c => c.client_type === 'standalone').length,
    active: clients.filter(c => c.status === 'active').length,
  };

  if (!['dpo', 'super_admin'].includes(user?.role)) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <Card>
            <CardContent className="p-12 text-center">
              <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h2 className="text-xl font-semibold mb-2">Access Restricted</h2>
              <p className="text-muted-foreground">Only DPO users can access client management.</p>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="client-management-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Client Management</h1>
            <p className="text-muted-foreground">Manage your clients and their entities</p>
          </div>
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="add-client-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Client
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Building2 className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-sm text-muted-foreground">Total Clients</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Layers className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.groups}</p>
                  <p className="text-sm text-muted-foreground">Group Companies</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <Building className="h-5 w-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.standalone}</p>
                  <p className="text-sm text-muted-foreground">Standalone</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <Users className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.active}</p>
                  <p className="text-sm text-muted-foreground">Active</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search clients..."
                  className="pl-9"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="search-clients"
                />
              </div>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-[180px]" data-testid="filter-type">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {CLIENT_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Clients Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Industry</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead>Entities</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : filteredClients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <Building2 className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                      <p className="text-muted-foreground">No clients found</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredClients.map((client) => (
                    <TableRow
                      key={client.id}
                      className="cursor-pointer hover:bg-muted/50"
                      data-testid={`client-row-${client.id}`}
                      onClick={() => navigate(`/admin/clients/${client.id}/entities`)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-primary/10">
                            <Building2 className="h-4 w-4 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">{client.client_name}</p>
                            <p className="text-xs text-muted-foreground font-mono">{client.client_code}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {CLIENT_TYPES.find(t => t.value === client.client_type)?.label || client.client_type}
                        </Badge>
                      </TableCell>
                      <TableCell>{client.industry || '-'}</TableCell>
                      <TableCell>{client.country || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{client.entity_count || 0} entities</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={STATUS_COLORS[client.status] || STATUS_COLORS.active}>
                          {client.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              data-testid={`client-menu-${client.id}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/admin/clients/${client.id}/entities`);
                            }}>
                              <Eye className="h-4 w-4 mr-2" />
                              View Entities
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              openEditDialog(client);
                            }}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              (async () => {
                                try {
                                  toast.info('Preparing full data export...');
                                  const res = await exportAPI.clientFullExport(client.id);
                                  const url = window.URL.createObjectURL(new Blob([res.data]));
                                  const a = document.createElement('a'); a.href = url;
                                  a.download = res.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') || `${client.client_name}_Export.zip`;
                                  document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
                                  toast.success('Data exported successfully');
                                } catch { toast.error('Export failed'); }
                              })();
                            }} data-testid={`export-client-data-${client.id}`}>
                              <Download className="h-4 w-4 mr-2" />
                              Export All Data
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                setClientToDelete(client);
                                setDeleteDialogOpen(true);
                              }}
                              className="text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingClient ? 'Edit Client' : 'Add New Client'}</DialogTitle>
              <DialogDescription>
                {editingClient ? 'Update client information' : 'Create a new client. For standalone/NGO/government types, a primary entity will be auto-created.'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="client_name">Client Name *</Label>
                  <Input
                    id="client_name"
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                    placeholder="e.g., LG Group of Companies"
                    required
                    data-testid="client-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="client_type">Client Type *</Label>
                  <Select value={formData.client_type} onValueChange={(v) => setFormData({ ...formData, client_type: v })}>
                    <SelectTrigger data-testid="client-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CLIENT_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>
                          <div>
                            <p>{t.label}</p>
                            <p className="text-xs text-muted-foreground">{t.description}</p>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="onboarding">Onboarding</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="maturity_tier">Maturity Assessment Tier</Label>
                  <Select value={formData.maturity_tier} onValueChange={(v) => setFormData({ ...formData, maturity_tier: v })}>
                    <SelectTrigger data-testid="maturity-tier-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MATURITY_TIERS.map(t => (
                        <SelectItem key={t.value} value={t.value}>
                          <div>
                            <p className="font-medium">{t.label}</p>
                            <p className="text-xs text-muted-foreground">{t.description}</p>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="subscription_tier">Subscription Tier</Label>
                  <Select
                    value={formData.subscription_tier}
                    onValueChange={(v) => setFormData({ ...formData, subscription_tier: v })}
                  >
                    <SelectTrigger data-testid="subscription-tier-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="starter">
                        <div>
                          <p className="font-medium">Starter</p>
                          <p className="text-xs text-muted-foreground">Core compliance essentials</p>
                        </div>
                      </SelectItem>
                      <SelectItem value="sme">
                        <div>
                          <p className="font-medium">SME</p>
                          <p className="text-xs text-muted-foreground">Enhanced features for growing businesses</p>
                        </div>
                      </SelectItem>
                      <SelectItem value="enterprise">
                        <div>
                          <p className="font-medium">Enterprise</p>
                          <p className="text-xs text-muted-foreground">Full suite</p>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {!editingClient && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Modules will be seeded from this tier's template at creation. After that,
                      you can customise modules per client in System Settings → Module Configuration.
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="industry">Industry</Label>
                  <Select value={formData.industry} onValueChange={(v) => setFormData({ ...formData, industry: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {INDUSTRIES.map(i => (
                        <SelectItem key={i} value={i}>{i}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="country">Country</Label>
                  <Select value={formData.country} onValueChange={(v) => setFormData({ ...formData, country: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Full address"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_email">Contact Email</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    placeholder="contact@company.com"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_phone">Contact Phone</Label>
                  <Input
                    id="contact_phone"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    placeholder="+1 (876) 555-0123"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="https://www.company.com"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes about this client"
                    rows={3}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-client-btn">
                  {editingClient ? 'Update Client' : 'Create Client'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Client</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete "{clientToDelete?.client_name}"? This will also delete all entities under this client. This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
